/**
 * Send-email toggle plus rich-text body, file upload, and attachment list for RMA modals that support customer email.
 */
import { observer } from "mobx-react";
import { Checkbox } from "../../../../../components/inputs/checkbox/checkbox";
import { EDITOR_VARIANT, RichTextEditor } from "../../../../../components/rich-text-editor/rich-text-editor";
import ListLayout from "../../../../../components/layouts/list-layout/list-layout";
import FileUpload from "../../../../../components/file-upload/file-upload";
import { FormField } from "../../../../../components/form-field/form-field";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import type { IRmaActionEmailNotificationStore } from "../../rma-types";
import RmaEmailAttachments from "./rma-email-attachments/rma-email-attachments";

interface IProps {
	emailStore: IRmaActionEmailNotificationStore;
	isDisabled?: boolean;
}

const RmaActionModalEmailSection: React.FC<IProps> = observer(({emailStore, isDisabled}) => {
	const {getTranslation} = useDashboardTranslation();
	const scope = "dashboard-returns-rma-card";
	const approveRequestScope = "dashboard-approve-request-popup";

	const onUploadFile = (fileData: File, fileContent: string | ArrayBuffer | null | undefined) => {
		emailStore.addEmailAttachment({
			name: fileData.name,
			type: fileData.type,
			base64: (fileContent as string).split(",")[1],
		});
	};

	return (
		<>
			<Checkbox
				label={getTranslation("SEND_EMAIL_NOTIFICATION", approveRequestScope)}
				checked={emailStore.sendEmailNotification}
				onChange={emailStore.setSendEmailNotification}
				disabled={!emailStore.hasCustomerEmail() || isDisabled}
			/>
			<ListLayout>
				{emailStore.sendEmailNotification && (
					<>
						<FormField label={getTranslation("ADD_DETAILS_TO_EMAIL", scope)}>
							<RichTextEditor
								maxHeight={200}
								value={emailStore.emailAdditionalContent ?? ""}
								onChange={emailStore.setEmailAdditionalContent}
								variant={EDITOR_VARIANT.EMAIL_EDITOR_ONLY}
								disabled={isDisabled}
							/>
						</FormField>

						<FormField
							label={getTranslation("UPLOAD_FILES", approveRequestScope)}
							instructions={getTranslation("ATTACH_FILES_MAX_SIZE", approveRequestScope, ["9"])}
						>
							<FileUpload
								fileTypes={["*"]}
								uploadFile={onUploadFile}
								readType="dataUrl"
								multiple
								disabled={isDisabled}
								buttonText={getTranslation("UPLOAD_FILE", scope)}
							/>
						</FormField>

						{emailStore.emailAttachments.length > 0 && <RmaEmailAttachments emailStore={emailStore}/>}
					</>
				)}
			</ListLayout>
		</>
	);
});

export default RmaActionModalEmailSection;
